import {useState, useEffect} from 'react';
import {useSearchParams, useNavigate} from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    TextField,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    Grid,
    Alert,
} from '@mui/material';
import {
    Add as AddIcon,
    ContentCopy as CopyIcon,
    Download as DownloadIcon,
    UploadFile as UploadFileIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import {toast} from 'react-toastify';
import {licenseApi} from 'utils/api';
import {toLocalDateTimeInput, toUtcDateTime} from 'utils/dateTime';

const LicenseType = {
    DEV: 'DEV',
    PRODUCTION: 'PRODUCTION',
};

const DomainPolicy = {
    SINGLE: 'SINGLE',
    MULTI: 'MULTI',
    UNLIMITED: 'UNLIMITED',
};

const BindingMode = {
    STRICT: 'STRICT',
    SOFT: 'SOFT',
    NONE: 'NONE',
};

const CoreCountMode = {
    PHYSICAL: 'PHYSICAL',
    LOGICAL: 'LOGICAL',
};

const LICENSE_TYPE_DEFAULTS = {
    DEV: {maxCores: 4, domainPolicy: 'UNLIMITED'},
    PRODUCTION: {maxCores: 0, domainPolicy: 'UNLIMITED'},
};

const PERIOD_OPTIONS = [
    {value: '1m', label: '1개월'},
    {value: '3m', label: '3개월'},
    {value: '6m', label: '6개월'},
    {value: '12m', label: '12개월'},
    {value: 'custom', label: '직접입력'},
];

// 기간에 따른 종료일시 계산
const calculateEndDateTime = (startDateTime, period) => {
    if (!startDateTime || period === 'custom') return '';
    const start = new Date(startDateTime);
    const months = parseInt(period.replace('m', ''), 10);
    start.setMonth(start.getMonth() + months);
    return toLocalDateTimeInput(start);
};

export default function LicenseGeneratePage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [newDomain, setNewDomain] = useState('');
    const [newMacAddress, setNewMacAddress] = useState('');
    const [generatedLicense, setGeneratedLicense] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 기타제품(EXTERNAL) 전용
    const [licenseKey, setLicenseKey] = useState('');
    const [licenseFile, setLicenseFile] = useState(null);

    // 신규 추가 / 전환 관련
    const [parentId, setParentId] = useState(null);
    const [actionType, setActionType] = useState('NEW');
    const [memo, setMemo] = useState('');
    const [sourceLicense, setSourceLicense] = useState(null);

    // 기간 프리셋 (1개월, 3개월, 6개월, 12개월, 직접입력)
    const [periodPreset, setPeriodPreset] = useState('3m');

    const [formData, setFormData] = useState({
        customerName: '',
        projectName: '',
        productId: '',
        licenseType: LicenseType.PRODUCTION,
        isPerpetual: true,
        validFrom: toLocalDateTimeInput(),
        validUntil: '',
        maxCores: 0,
        coreCountMode: CoreCountMode.LOGICAL,
        domainPolicy: DomainPolicy.UNLIMITED,
        allowedDomains: [],
        bindingMode: BindingMode.NONE,
        allowedMacAddresses: [],
    });

    useEffect(() => {
        loadProducts();
    }, []);

    // 쿼리파라미터에서 원본 라이선스 정보 로드
    useEffect(() => {
        const fromId = searchParams.get('from');
        const action = searchParams.get('action');
        if (!fromId) return;

        const loadSource = async () => {
            try {
                const response = await licenseApi.get(fromId);
                const src = response.data;
                setSourceLicense(src);
                setParentId(src.id);
                setActionType(action === 'convert' ? 'CONVERT' : 'RENEW');

                const isConvert = action === 'convert';
                const newType = isConvert
                    ? (src.license_type === 'DEV' ? LicenseType.PRODUCTION : LicenseType.DEV)
                    : src.license_type;

                const now = new Date();
                const defaults = LICENSE_TYPE_DEFAULTS[newType];

                const updates = {
                    customerName: src.customer_name,
                    projectName: src.project_name,
                    productId: src.product_id,
                    licenseType: newType,
                    maxCores: isConvert ? defaults.maxCores : (src.max_cores ?? defaults.maxCores),
                    coreCountMode: CoreCountMode.LOGICAL,
                    domainPolicy: isConvert ? defaults.domainPolicy : (src.domain_policy || defaults.domainPolicy),
                    allowedDomains: (!isConvert && src.allowed_domains) ? src.allowed_domains.split(',').filter(Boolean) : [],
                    bindingMode: isConvert ? BindingMode.NONE : (src.binding_mode || BindingMode.NONE),
                    allowedMacAddresses: [],
                };

                if (newType === LicenseType.DEV) {
                    updates.isPerpetual = false;
                    const nowStr = toLocalDateTimeInput(now);
                    updates.validFrom = nowStr;
                    updates.validUntil = calculateEndDateTime(nowStr, '3m');
                    setPeriodPreset('3m');
                } else if (newType === LicenseType.PRODUCTION) {
                    if (!isConvert && src.valid_until) {
                        // 신규 추가: 기존 기간 유지
                        updates.isPerpetual = false;
                        // 기존 데이터가 date 형식이면 datetime으로 변환
                        const fromVal = src.valid_from || now;
                        const untilVal = src.valid_until;
                        updates.validFrom = toLocalDateTimeInput(fromVal);
                        updates.validUntil = untilVal.includes('T')
                            ? toLocalDateTimeInput(untilVal) : `${untilVal}T23:59`;
                        setPeriodPreset('custom');
                    } else {
                        updates.isPerpetual = true;
                        updates.validFrom = '';
                        updates.validUntil = '';
                    }
                }

                setFormData(prev => ({...prev, ...updates}));
            } catch (err) {
                toast.error('원본 라이선스 정보를 불러올 수 없습니다.');
            }
        };
        loadSource();
    }, [searchParams]);

    // 쿼리파라미터에서 고객사명 프리필
    useEffect(() => {
        const customer = searchParams.get('customer');
        if (customer) {
            setFormData(prev => ({...prev, customerName: customer}));
        }
    }, [searchParams]);

    const loadProducts = async () => {
        try {
            const response = await licenseApi.getProducts();
            setProducts(response.data.products);
            if (response.data.products.length > 0 && !formData.productId) {
                setFormData(prev => ({...prev, productId: response.data.products[0].id}));
            }
        } catch (err) {
            toast.error('제품 목록을 불러오는데 실패했습니다.');
        }
    };

    const selectedProduct = products.find(p => p.id === formData.productId);
    const isExternal = selectedProduct?.type === 'EXTERNAL';
    const isInternalModule = selectedProduct?.type === 'INTERNAL_MODULE';

    const handleChange = (field, value) => {
        setFormData(prev => ({...prev, [field]: value}));
    };

    const handleLicenseTypeChange = (type) => {
        const defaults = LICENSE_TYPE_DEFAULTS[type];
        const nowStr = toLocalDateTimeInput();
        const updates = {
            licenseType: type,
            maxCores: defaults.maxCores,
            domainPolicy: defaults.domainPolicy,
        };

        if (type === LicenseType.DEV) {
            updates.isPerpetual = false;
            updates.validFrom = nowStr;
            updates.validUntil = calculateEndDateTime(nowStr, periodPreset);
        } else {
            updates.isPerpetual = true;
            updates.validFrom = '';
            updates.validUntil = '';
        }

        setFormData(prev => ({...prev, ...updates}));
    };

    const handlePerpetualChange = (isPerpetual) => {
        if (isPerpetual) {
            setFormData(prev => ({...prev, isPerpetual: true, validFrom: '', validUntil: ''}));
        } else {
            const nowStr = toLocalDateTimeInput();
            setFormData(prev => ({
                ...prev,
                isPerpetual: false,
                validFrom: nowStr,
                validUntil: calculateEndDateTime(nowStr, periodPreset),
            }));
        }
    };

    // 기간 프리셋 변경 핸들러
    const handlePeriodChange = (preset) => {
        setPeriodPreset(preset);
        if (preset !== 'custom' && formData.validFrom) {
            setFormData(prev => ({
                ...prev,
                validUntil: calculateEndDateTime(prev.validFrom, preset),
            }));
        }
    };

    // 시작일시 변경 시 종료일시 자동 계산
    const handleValidFromChange = (value) => {
        const updates = {validFrom: value};
        if (periodPreset !== 'custom' && value) {
            updates.validUntil = calculateEndDateTime(value, periodPreset);
        }
        setFormData(prev => ({...prev, ...updates}));
    };

    const addDomain = () => {
        if (newDomain.trim() && !formData.allowedDomains.includes(newDomain.trim())) {
            setFormData(prev => ({
                ...prev,
                allowedDomains: [...prev.allowedDomains, newDomain.trim()]
            }));
            setNewDomain('');
        }
    };

    const removeDomain = (domain) => {
        setFormData(prev => ({
            ...prev,
            allowedDomains: prev.allowedDomains.filter(d => d !== domain)
        }));
    };

    const isValidMacAddress = (mac) => {
        const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
        return macRegex.test(mac);
    };

    const addMacAddress = () => {
        const normalizedMac = newMacAddress.trim().toUpperCase().replace(/-/g, ':');
        if (normalizedMac && isValidMacAddress(normalizedMac) && !formData.allowedMacAddresses.includes(normalizedMac)) {
            setFormData(prev => ({
                ...prev,
                allowedMacAddresses: [...prev.allowedMacAddresses, normalizedMac]
            }));
            setNewMacAddress('');
        } else if (newMacAddress.trim() && !isValidMacAddress(newMacAddress.trim())) {
            toast.error('올바른 MAC 주소 형식이 아닙니다. (예: 00:1A:2B:3C:4D:5E)');
        }
    };

    const removeMacAddress = (mac) => {
        setFormData(prev => ({
            ...prev,
            allowedMacAddresses: prev.allowedMacAddresses.filter(m => m !== mac)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            let response;

            if (isExternal) {
                // 기타제품: multipart/form-data
                const fd = new FormData();
                fd.append('customerName', formData.customerName);
                fd.append('projectName', formData.projectName);
                fd.append('productId', formData.productId);
                fd.append('licenseType', formData.licenseType);

                if (formData.licenseType === LicenseType.DEV || !formData.isPerpetual) {
                    if (formData.validFrom) fd.append('validFrom', toUtcDateTime(formData.validFrom));
                    if (formData.validUntil) fd.append('validUntil', toUtcDateTime(formData.validUntil));
                }

                if (licenseKey.trim()) fd.append('licenseKey', licenseKey.trim());
                if (licenseFile) fd.append('licenseFile', licenseFile);

                if (parentId) {
                    fd.append('parentId', parentId);
                    fd.append('actionType', actionType);
                }
                if (memo.trim()) fd.append('memo', memo.trim());

                response = await licenseApi.createMultipart(fd);
                toast.success('라이선스가 등록되었습니다.');
            } else {
                // 자사제품: JSON
                const payload = {
                    customerName: formData.customerName,
                    projectName: formData.projectName,
                    productId: formData.productId,
                    licenseType: formData.licenseType,
                    maxCores: isInternalModule ? 0 : Number(formData.maxCores),
                    coreCountMode: formData.coreCountMode,
                    domainPolicy: isInternalModule ? DomainPolicy.UNLIMITED : formData.domainPolicy,
                    allowedDomains: isInternalModule ? [] : formData.allowedDomains,
                    bindingMode: formData.bindingMode,
                    allowedMacAddresses: formData.allowedMacAddresses,
                };

                if (formData.licenseType === LicenseType.DEV || !formData.isPerpetual) {
                    payload.validFrom = toUtcDateTime(formData.validFrom);
                    payload.validUntil = toUtcDateTime(formData.validUntil);
                }

                if (parentId) {
                    payload.parentId = parentId;
                    payload.actionType = actionType;
                }
                if (memo.trim()) {
                    payload.memo = memo.trim();
                }

                response = await licenseApi.create(payload);
                toast.success('라이선스가 생성되었습니다.');
            }

            navigate('/');
        } catch (error) {
            toast.error(error.response?.data?.error || '라이선스 생성에 실패했습니다.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('클립보드에 복사되었습니다.');
    };

    const downloadLicense = () => {
        if (!generatedLicense) return;
        const blob = new Blob([generatedLicense], {type: 'text/plain'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `license-${formData.customerName || 'unknown'}.lic`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('라이선스 파일 다운로드 완료');
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                라이선스 발급
            </Typography>

            {sourceLicense && (
                <Alert
                    severity={actionType === 'CONVERT' ? 'info' : 'success'}
                    sx={{mb: 2}}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => {
                                setSourceLicense(null);
                                setParentId(null);
                                setActionType('NEW');
                                setMemo('');
                                navigate('/generate', {replace: true});
                            }}
                        >
                            취소
                        </Button>
                    }
                >
                    <strong>{sourceLicense.customer_name} - {sourceLicense.project_name}</strong>
                    {' '}라이선스에서{' '}
                    <strong>{actionType === 'CONVERT' ? '전환' : '신규 추가'}</strong>
                </Alert>
            )}

            <form onSubmit={handleSubmit}>
                <Grid container spacing={{xs: 2, md: 3}}>
                    {/* 메모 (신규 추가/전환 시) */}
                    {parentId && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="발급 사유 / 메모"
                                placeholder="예: 계약 연장, DEV→정식 전환 등"
                                value={memo}
                                onChange={(e) => setMemo(e.target.value)}
                            />
                        </Grid>
                    )}

                    {/* 섹션 1: 기본 정보 */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                            기본 정보
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>제품명</InputLabel>
                            <Select
                                value={formData.productId}
                                label="제품명"
                                onChange={(e) => handleChange('productId', e.target.value)}
                            >
                                {products.map((product) => (
                                    <MenuItem key={product.id} value={product.id}>
                                        {product.name} {product.version ? `(v${product.version})` : ''}
                                        {product.type === 'EXTERNAL' ? ' [기타]' : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>라이선스 구분</InputLabel>
                            <Select
                                value={formData.licenseType}
                                label="라이선스 구분"
                                onChange={(e) => handleLicenseTypeChange(e.target.value)}
                            >
                                <MenuItem value={LicenseType.DEV}>개발</MenuItem>
                                <MenuItem value={LicenseType.PRODUCTION}>정식</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="고객사"
                            value={formData.customerName}
                            onChange={(e) => handleChange('customerName', e.target.value)}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="사업명"
                            value={formData.projectName}
                            onChange={(e) => handleChange('projectName', e.target.value)}
                            required
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{my: 1}}/>
                    </Grid>

                    {/* 섹션 2: 유효 기간 */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                            유효 기간
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            입력 시간은 현재 브라우저의 로컬 시간으로 표시되며, 라이선스와 서버에는 UTC ISO 8601 형식으로 저장됩니다.
                        </Typography>
                    </Grid>

                    {formData.licenseType === LicenseType.PRODUCTION && (
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>기간 유형</InputLabel>
                                <Select
                                    value={formData.isPerpetual ? 'perpetual' : 'limited'}
                                    label="기간 유형"
                                    onChange={(e) => handlePerpetualChange(e.target.value === 'perpetual')}
                                >
                                    <MenuItem value="perpetual">영구</MenuItem>
                                    <MenuItem value="limited">기간 제한</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    {formData.licenseType === LicenseType.DEV && (
                        <Grid item xs={12} md={4}>
                            <Typography variant="body2" color="text.secondary" sx={{pt: 1}}>
                                개발 라이선스는 기간 제한이 필수입니다.
                            </Typography>
                        </Grid>
                    )}

                    {(formData.licenseType === LicenseType.DEV || !formData.isPerpetual) && (
                        <>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>유효 기간</InputLabel>
                                    <Select
                                        value={periodPreset}
                                        label="유효 기간"
                                        onChange={(e) => handlePeriodChange(e.target.value)}
                                    >
                                        {PERIOD_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={8} />
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="datetime-local"
                                    label="시작일시"
                                    value={formData.validFrom}
                                    onChange={(e) => handleValidFromChange(e.target.value)}
                                    InputLabelProps={{shrink: true}}
                                    required
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="datetime-local"
                                    label="종료일시"
                                    value={formData.validUntil}
                                    onChange={(e) => handleChange('validUntil', e.target.value)}
                                    InputLabelProps={{shrink: true}}
                                    disabled={periodPreset !== 'custom'}
                                />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}>
                        <Divider sx={{my: 1}}/>
                    </Grid>

                    {/* 기타제품: 라이선스 키/파일 입력 */}
                    {isExternal && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    라이선스 정보
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="라이선스 키"
                                    placeholder="라이선스 키를 입력하세요"
                                    value={licenseKey}
                                    onChange={(e) => setLicenseKey(e.target.value)}
                                    multiline
                                    rows={4}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<UploadFileIcon/>}
                                    >
                                        라이선스 파일 업로드
                                        <input
                                            type="file"
                                            hidden
                                            onChange={(e) => setLicenseFile(e.target.files[0] || null)}
                                        />
                                    </Button>
                                    {licenseFile && (
                                        <Chip
                                            label={licenseFile.name}
                                            onDelete={() => setLicenseFile(null)}
                                            deleteIcon={<CloseIcon/>}
                                            variant="outlined"
                                        />
                                    )}
                                    {!licenseFile && (
                                        <Typography variant="body2" color="text.secondary">
                                            키 또는 파일 중 하나 이상 입력
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        </>
                    )}

                    {/* 섹션 3: 제약 조건 - 코어 (자사제품만) */}
                    {!isExternal && !isInternalModule && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                코어 제한
                            </Typography>
                        </Grid>
                    )}

                    {!isExternal && (
                        <>
                            {!isInternalModule && (
                                <>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>코어 제한</InputLabel>
                                    <Select
                                        value={formData.maxCores === 0 ? 'unlimited' : 'limited'}
                                        label="코어 제한"
                                        onChange={(e) => {
                                            if (e.target.value === 'unlimited') {
                                                handleChange('maxCores', 0);
                                            } else {
                                                handleChange('maxCores', 4);
                                            }
                                        }}
                                    >
                                        <MenuItem value="unlimited">무제한</MenuItem>
                                        <MenuItem value="limited">제한</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {formData.maxCores > 0 && (
                                <>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="최대 코어 수"
                                            value={formData.maxCores}
                                            onChange={(e) => handleChange('maxCores', e.target.value)}
                                            inputProps={{min: 1}}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>카운트 모드</InputLabel>
                                            <Select
                                                value={formData.coreCountMode}
                                                label="카운트 모드"
                                                onChange={(e) => handleChange('coreCountMode', e.target.value)}
                                            >
                                                <MenuItem value={CoreCountMode.LOGICAL}>Logical (HT 포함)</MenuItem>
                                                <MenuItem value={CoreCountMode.PHYSICAL}>Physical (물리 코어만)</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12}>
                                <Divider sx={{my: 1}}/>
                            </Grid>

                            {/* 섹션 4: 제약 조건 - 도메인 */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    도메인 정책
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>도메인 제한</InputLabel>
                                    <Select
                                        value={formData.domainPolicy === DomainPolicy.UNLIMITED ? 'unlimited' : 'limited'}
                                        label="도메인 제한"
                                        onChange={(e) => {
                                            handleChange('domainPolicy', e.target.value === 'unlimited' ? DomainPolicy.UNLIMITED : DomainPolicy.MULTI);
                                        }}
                                    >
                                        <MenuItem value="unlimited">무제한</MenuItem>
                                        <MenuItem value="limited">제한</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {formData.domainPolicy !== DomainPolicy.UNLIMITED && (
                                <Grid item xs={12}>
                                    <Box
                                        sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: 1, mb: 1}}>
                                        <TextField
                                            size="small"
                                            label="허용 도메인/IP"
                                            placeholder="*.intosoft.kr, 192.168.1.*"
                                            value={newDomain}
                                            onChange={(e) => setNewDomain(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addDomain();
                                                }
                                            }}
                                            helperText="도메인, IP 모두 가능. 와일드카드(*) 패턴 지원"
                                            sx={{flexGrow: 1}}
                                        />
                                        <Button
                                            type="button"
                                            variant={newDomain.trim() ? 'contained' : 'outlined'}
                                            onClick={addDomain}
                                            disabled={!newDomain.trim()}
                                            startIcon={<AddIcon/>}
                                            sx={{alignSelf: {xs: 'flex-end', sm: 'flex-start'}}}
                                        >
                                            추가
                                        </Button>
                                    </Box>
                                    <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                                        {formData.allowedDomains.map((domain) => (
                                            <Chip
                                                key={domain}
                                                label={domain}
                                                onDelete={() => removeDomain(domain)}
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Divider sx={{my: 1}}/>
                            </Grid>

                                </>
                            )}

                            {/* 섹션 5: 하드웨어 바인딩 */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    하드웨어 바인딩
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>하드웨어 바인딩</InputLabel>
                                    <Select
                                        value={formData.bindingMode === BindingMode.NONE ? 'off' : 'on'}
                                        label="하드웨어 바인딩"
                                        onChange={(e) => {
                                            handleChange('bindingMode', e.target.value === 'off' ? BindingMode.NONE : BindingMode.STRICT);
                                        }}
                                    >
                                        <MenuItem value="off">사용 안 함</MenuItem>
                                        <MenuItem value="on">사용</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {formData.bindingMode !== BindingMode.NONE && (
                                <Grid item xs={12}>
                                    <Box
                                        sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: 1, mb: 1}}>
                                        <TextField
                                            size="small"
                                            label="MAC 주소"
                                            placeholder="00:1A:2B:3C:4D:5E"
                                            value={newMacAddress}
                                            onChange={(e) => setNewMacAddress(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addMacAddress();
                                                }
                                            }}
                                            helperText="MAC 주소 형식: 00:1A:2B:3C:4D:5E 또는 00-1A-2B-3C-4D-5E"
                                            sx={{flexGrow: 1}}
                                        />
                                        <Button
                                            type="button"
                                            variant={newMacAddress.trim() ? 'contained' : 'outlined'}
                                            onClick={addMacAddress}
                                            disabled={!newMacAddress.trim()}
                                            startIcon={<AddIcon/>}
                                            sx={{alignSelf: {xs: 'flex-end', sm: 'flex-start'}}}
                                        >
                                            추가
                                        </Button>
                                    </Box>
                                    <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                                        {formData.allowedMacAddresses.map((mac) => (
                                            <Chip
                                                key={mac}
                                                label={mac}
                                                onDelete={() => removeMacAddress(mac)}
                                                variant="outlined"
                                                color="primary"
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                            )}
                        </>
                    )}

                    <Grid item xs={12}>
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            fullWidth
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? '처리 중...' : (isExternal ? '라이선스 등록' : '라이선스 생성')}
                        </Button>
                    </Grid>
                </Grid>
            </form>

            {/* 생성/등록된 라이선스 */}
            {generatedLicense !== null && (
                <Box sx={{mt: 4}}>
                    <Divider sx={{mb: 3}}/>
                    <Card variant="outlined">
                        <CardContent>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {isExternal ? '등록된 라이선스' : '생성된 라이선스'}
                                </Typography>
                                <Box>
                                    {generatedLicense && (
                                        <>
                                            <Tooltip title="복사">
                                                <IconButton onClick={() => copyToClipboard(generatedLicense)}
                                                            size="small">
                                                    <CopyIcon/>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="다운로드">
                                                <IconButton onClick={downloadLicense} size="small">
                                                    <DownloadIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </Box>
                            </Box>
                            {generatedLicense ? (
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={6}
                                    value={generatedLicense}
                                    InputProps={{readOnly: true}}
                                    sx={{fontFamily: 'monospace', fontSize: '0.75rem'}}
                                />
                            ) : (
                                <Alert severity="success">
                                    라이선스가 등록되었습니다. 목록에서 확인할 수 있습니다.
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            )}
        </Box>
    );
}
